/**
 * Admin audit over session_events: when each session began and ended, from
 * which device and IP, and why it ended.
 *
 * Gated by the `login_sessions` Access panel (enforced on the server too — the
 * route below is only the UI half). Scoped server-side to the caller's own
 * head office.
 */
import { useCallback, useEffect, useState } from "react";
import api from "../api/api";

interface EventRow {
  id: string;
  user_id: string;
  session_id: string;
  platform: string;
  device_name: string | null;
  os: string | null;
  client_version: string | null;
  ip_address: string | null;
  event: string;
  at: string | null;
}

const PLATFORM_LABELS: Record<string, string> = {
  teamlio_web: "Teamlio Web",
  messenger_web: "Messenger (Browser)",
  messenger_desktop: "Messenger Desktop",
  workmonitor: "Work Monitor",
  teamlio_mobile: "Mobile App",
};

const EVENT_LABELS: Record<string, string> = {
  login: "Signed in",
  logout: "Signed out",
  evicted: "Signed out — used on another device",
  revoked: "Signed out remotely",
};

const PAGE = 50;

export default function LoginSessions() {
  const [rows, setRows] = useState<EventRow[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [platform, setPlatform] = useState("");
  const [userId, setUserId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/api/auth/sessions/history", {
        params: {
          limit: PAGE,
          offset,
          ...(platform ? { platform } : {}),
          ...(userId ? { user_id: userId } : {}),
        },
      });
      setRows(data?.events ?? []);
      setTotal(data?.total ?? 0);
      setError(null);
    } catch (e: any) {
      setError(
        e?.response?.status === 403
          ? "You do not have access to login history."
          : "Could not load login history.",
      );
    } finally {
      setLoading(false);
    }
  }, [offset, platform, userId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-semibold">Login sessions</h1>

      <div className="flex gap-2">
        <input
          className="rounded-md border px-2 py-1 text-sm"
          placeholder="Filter by user id"
          value={userId}
          onChange={(e) => {
            setOffset(0);
            setUserId(e.target.value);
          }}
        />
        <select
          className="rounded-md border px-2 py-1 text-sm"
          value={platform}
          onChange={(e) => {
            setOffset(0);
            setPlatform(e.target.value);
          }}
        >
          <option value="">All platforms</option>
          {Object.entries(PLATFORM_LABELS).map(([k, label]) => (
            <option key={k} value={k}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {error && <div className="text-sm text-red-600">{error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left">
              <th className="p-2">When</th>
              <th className="p-2">User</th>
              <th className="p-2">Platform</th>
              <th className="p-2">Device</th>
              <th className="p-2">IP</th>
              <th className="p-2">Event</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t">
                <td className="p-2">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                <td className="p-2">{r.user_id}</td>
                <td className="p-2">{PLATFORM_LABELS[r.platform] ?? r.platform}</td>
                <td className="p-2">
                  {[r.device_name, r.os, r.client_version].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="p-2">{r.ip_address || "—"}</td>
                <td className="p-2">{EVENT_LABELS[r.event] ?? r.event}</td>
              </tr>
            ))}
            {!loading && rows.length === 0 && (
              <tr>
                <td className="p-2 text-muted-foreground" colSpan={6}>
                  No login activity recorded for this filter.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="flex items-center gap-3 text-sm">
        <button
          type="button"
          className="rounded-md border px-3 py-1 disabled:opacity-50"
          disabled={offset === 0 || loading}
          onClick={() => setOffset(Math.max(0, offset - PAGE))}
        >
          Previous
        </button>
        <span>
          {total === 0 ? 0 : offset + 1}–{Math.min(offset + PAGE, total)} of {total}
        </span>
        <button
          type="button"
          className="rounded-md border px-3 py-1 disabled:opacity-50"
          disabled={offset + PAGE >= total || loading}
          onClick={() => setOffset(offset + PAGE)}
        >
          Next
        </button>
      </div>
    </div>
  );
}
